import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, Tooltip, CartesianGrid,
} from "recharts";
import {
  ArrowLeftIcon, CopyIcon, TrashIcon, FloppyDiskIcon, LinkSimpleIcon, StackIcon,
  DeviceMobileIcon, GlobeIcon, PauseIcon, PlayIcon, ProhibitIcon, LinkBreakIcon, PrinterIcon,
} from "@phosphor-icons/react";
import api, { resolveUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import ScanMap from "@/components/ScanMap";
import HubEditor from "@/components/HubEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_STYLE = {
  active: "border-primary/40 text-primary",
  printing: "border-accent/40 text-accent",
  paused: "border-white/30 text-white/60",
  disabled: "border-destructive/40 text-destructive",
  unallocated: "border-white/20 text-white/40",
};

function Panel({ title, children, className = "", testid }) {
  return (
    <div data-testid={testid} className={`border border-border bg-card p-6 ${className}`}>
      {title && <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-4">{title}</p>}
      {children}
    </div>
  );
}

function BreakdownList({ data, accent = "text-primary" }) {
  const max = Math.max(1, ...data.map((d) => d.value));
  if (!data.length) return <p className="font-mono text-xs text-white/30">No data yet</p>;
  return (
    <div className="space-y-3">
      {data.map((d) => (
        <div key={d.name}>
          <div className="flex justify-between font-mono text-xs mb-1">
            <span className="text-white/70 truncate pr-2">{d.name}</span>
            <span className={accent}>{d.value}</span>
          </div>
          <div className="h-1.5 bg-secondary/60"><div className="h-full bg-primary/70" style={{ width: `${(d.value / max) * 100}%` }} /></div>
        </div>
      ))}
    </div>
  );
}

export default function TagDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tag, setTag] = useState(null);
  const [stats, setStats] = useState(null);
  const [label, setLabel] = useState("");
  const [mode, setMode] = useState("direct");
  const [dest, setDest] = useState("");
  const [status, setStatus] = useState("active");
  const [theme, setTheme] = useState({});
  const [links, setLinks] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/tags/${id}`);
      setTag(data); setLabel(data.label); setMode(data.mode);
      setDest(data.destination_url || ""); setStatus(data.status || "active");
      setTheme(data.hub_theme || {}); setLinks(data.links || []);
      const a = await api.get(`/tags/${id}/analytics`);
      setStats(a.data);
    } catch { toast.error("Could not load this code"); navigate("/admin"); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const changeMode = async (m) => {
    setMode(m);
    try { await api.put(`/tags/${id}`, { mode: m }); } catch { toast.error("Could not switch mode"); }
  };

  const saveDirect = async () => {
    if (!dest.trim()) return toast.error("Direct mode needs a destination URL");
    setSaving(true);
    try { await api.put(`/tags/${id}`, { label: label.trim(), mode: "direct", destination_url: dest.trim() }); toast.success("Saved"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  const setLifecycle = async (s) => {
    setStatus(s);
    try { await api.put(`/tags/${id}`, { status: s }); toast.success(`Status: ${s}`); }
    catch { toast.error("Could not update status"); }
  };

  const unbind = async () => {
    try { await api.post(`/tags/${id}/unbind`); toast.success("Code unbound from your account"); navigate("/admin"); }
    catch { toast.error("Could not unbind"); }
  };

  const delTag = async () => {
    try { await api.delete(`/tags/${id}`); toast.success("Code deleted"); navigate("/admin"); }
    catch { toast.error("Could not delete"); }
  };

  if (!tag) return <Layout><p className="font-mono text-sm text-primary animate-pulse">LOADING…</p></Layout>;

  const StatCell = ({ label, value, color = "" }) => (
    <div className="p-6 border-r border-border last:border-r-0">
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">{label}</p>
      <p className={`font-mono text-4xl mt-2 ${color}`}>{value ?? "—"}</p>
    </div>
  );

  return (
    <Layout>
      <button data-testid="detail-back-btn" onClick={() => navigate("/admin")} className="flex items-center gap-1.5 font-mono text-xs text-white/50 hover:text-primary transition-colors mb-6">
        <ArrowLeftIcon weight="bold" /> ALL CODES
      </button>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading font-black text-4xl tracking-tighter leading-none">{tag.label}</h1>
            <Badge variant="outline" className={`font-mono text-[10px] uppercase ${STATUS_STYLE[status] || ""}`} data-testid="detail-status-badge">{status}</Badge>
          </div>
          <p className="font-mono text-primary text-sm mt-2">/{tag.code}</p>
        </div>
      </div>

      {/* Permanent URL strip */}
      <div className="border border-border bg-card flex items-center justify-between gap-4 px-6 py-4 mb-8">
        <div className="min-w-0">
          <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40 mb-1">Permanent scan URL · one code, forever</p>
          <p data-testid="detail-resolve-url" className="font-mono text-sm truncate text-white/80">{resolveUrl(tag.code)}</p>
        </div>
        <Button data-testid="detail-copy-url-btn" variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(resolveUrl(tag.code)); toast.success("Copied"); }} className="border-border bg-transparent hover:bg-secondary hover:text-white font-mono text-xs shrink-0">
          <CopyIcon weight="bold" className="mr-1.5" /> COPY
        </Button>
      </div>

      <Tabs defaultValue="destination">
        <TabsList className="bg-secondary/40 border border-border">
          <TabsTrigger value="destination" data-testid="tab-destination">Destination</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
        </TabsList>

        {/* DESTINATION */}
        <TabsContent value="destination" className="mt-6">
          <div className="border border-border bg-card p-6 mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
            <div className="flex-1">
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60 mb-2">Mode</p>
              <div className="flex gap-3">
                {[{ id: "direct", icon: LinkSimpleIcon, t: "Direct redirect" }, { id: "hub", icon: StackIcon, t: "Hub page" }].map((m) => (
                  <button key={m.id} data-testid={`mode-${m.id}`} onClick={() => changeMode(m.id)}
                    className={`flex items-center gap-2 px-4 py-2.5 border transition-colors ${mode === m.id ? "border-primary bg-primary/5 text-primary" : "border-border text-white/60 hover:border-white/30"}`}>
                    <m.icon weight="bold" /> {m.t}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {mode === "direct" ? (
            <div className="border border-border bg-card p-6 space-y-5 max-w-xl" data-testid="direct-config">
              <div className="space-y-2">
                <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Label</label>
                <Input data-testid="detail-label-input" value={label} onChange={(e) => setLabel(e.target.value)} className="bg-secondary/40 border-border focus-visible:ring-primary" />
              </div>
              <div className="space-y-2">
                <label className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/60">Destination URL</label>
                <Input data-testid="detail-destination-input" value={dest} onChange={(e) => setDest(e.target.value)} placeholder="https://…" className="bg-secondary/40 border-border font-mono text-sm focus-visible:ring-primary" />
              </div>
              <Button data-testid="detail-save-btn" onClick={saveDirect} disabled={saving} className="bg-primary text-primary-foreground font-bold hover:bg-primary/90 transition-colors">
                <FloppyDiskIcon weight="bold" className="mr-1.5" /> {saving ? "SAVING…" : "SAVE"}
              </Button>
            </div>
          ) : (
            <HubEditor tagId={id} theme={theme} links={links} setTheme={setTheme} setLinks={setLinks} reload={load} />
          )}
        </TabsContent>

        {/* ANALYTICS */}
        <TabsContent value="analytics" className="mt-6 space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-5 border border-border bg-card">
            <StatCell label="Total scans" value={stats?.total_scans} color="text-primary" />
            <StatCell label="Unique" value={stats?.unique_visitors} />
            <StatCell label="Repeat" value={stats?.repeat_visitors} />
            <StatCell label="Link clicks" value={stats?.total_clicks} />
            <StatCell label="In-app" value={stats?.in_app_scans} color="text-accent" />
          </div>

          <Panel title="// Scans · last 14 days" testid="analytics-timeseries">
            <div className="h-52">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.timeseries || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" vertical={false} />
                  <XAxis dataKey="date" tick={{ fill: "#666", fontFamily: "JetBrains Mono", fontSize: 9 }} tickFormatter={(d) => d.slice(5)} axisLine={{ stroke: "#1f1f1f" }} tickLine={false} />
                  <Tooltip cursor={{ fill: "rgba(0,255,102,0.06)" }} contentStyle={{ background: "#0a0a0a", border: "1px solid #1f1f1f", fontFamily: "JetBrains Mono", fontSize: 12 }} labelStyle={{ color: "#00ff66" }} />
                  <Bar dataKey="scans" fill="#00ff66" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel title="// Scan map" testid="analytics-map">
            <div className="flex items-center justify-between mb-4 -mt-1">
              <span className="font-mono text-[11px] text-white/40">Where scans light up</span>
              <span className="font-mono text-[11px] text-primary">{stats?.located_scans ?? 0} located</span>
            </div>
            <ScanMap points={stats?.map_points || []} />
          </Panel>

          {mode === "hub" && (
            <Panel title="// Click-through per link" testid="analytics-link-clicks">
              {(stats?.link_clicks || []).length === 0 ? (
                <p className="font-mono text-xs text-white/30">No links yet.</p>
              ) : (
                <BreakdownList data={(stats.link_clicks).map((l) => ({ name: l.title + (l.enabled ? "" : " (off)"), value: l.clicks }))} />
              )}
            </Panel>
          )}

          <div className="grid sm:grid-cols-2 gap-6">
            <Panel title="// By country" testid="analytics-country"><BreakdownList data={stats?.by_country || []} /></Panel>
            <Panel title="// By city" testid="analytics-city"><BreakdownList data={stats?.by_city || []} /></Panel>
            <Panel title="// By device" testid="analytics-device"><BreakdownList data={stats?.by_device || []} /></Panel>
            <Panel title="// By OS" testid="analytics-os"><BreakdownList data={stats?.by_os || []} /></Panel>
            <Panel title="// By browser" testid="analytics-browser"><BreakdownList data={stats?.by_browser || []} /></Panel>
            <Panel title="// In-app browser share" testid="analytics-inapp"><BreakdownList data={stats?.by_in_app || []} /></Panel>
          </div>

          <Panel title="// Recent scans" testid="analytics-recent">
            {(stats?.recent || []).length === 0 ? (
              <p className="font-mono text-xs text-white/30">No scans recorded yet.</p>
            ) : (
              <div className="-mx-2">
                {stats.recent.map((s, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-2 py-2.5 border-b border-border/60 last:border-0">
                    <div className="flex items-center gap-2 min-w-0">
                      {s.device === "Mobile" ? <DeviceMobileIcon className="text-white/40 shrink-0" /> : <GlobeIcon className="text-white/40 shrink-0" />}
                      <span className="font-mono text-xs text-white/70 truncate">
                        {[s.os, s.browser].filter(Boolean).join(" · ") || "Unknown"}{s.city ? ` · ${s.city}` : s.country ? ` · ${s.country}` : ""}
                      </span>
                      {s.in_app_browser && <Badge variant="outline" className="font-mono text-[9px] border-accent/40 text-accent shrink-0">{s.in_app_name}</Badge>}
                    </div>
                    <span className="font-mono text-[11px] text-white/30 shrink-0">{new Date(s.timestamp).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </TabsContent>

        {/* SETTINGS */}
        <TabsContent value="settings" className="mt-6 space-y-6 max-w-xl">
          <Panel title="// Lifecycle" testid="settings-lifecycle">
            <p className="text-sm text-white/50 mb-4">Current status: <span className="font-mono text-white">{status}</span></p>
            <div className="grid grid-cols-2 gap-3">
              <Button data-testid="status-printing" variant="outline" onClick={() => setLifecycle("printing")} className="border-border bg-transparent hover:bg-secondary hover:text-white justify-start"><PrinterIcon weight="bold" className="mr-2 text-accent" /> Printing</Button>
              <Button data-testid="status-active" variant="outline" onClick={() => setLifecycle("active")} className="border-border bg-transparent hover:bg-secondary hover:text-white justify-start"><PlayIcon weight="bold" className="mr-2 text-primary" /> Activate</Button>
              <Button data-testid="status-paused" variant="outline" onClick={() => setLifecycle("paused")} className="border-border bg-transparent hover:bg-secondary hover:text-white justify-start"><PauseIcon weight="bold" className="mr-2" /> Pause</Button>
              <Button data-testid="status-disabled" variant="outline" onClick={() => setLifecycle("disabled")} className="border-border bg-transparent hover:bg-secondary hover:text-white justify-start"><ProhibitIcon weight="bold" className="mr-2 text-destructive" /> Disable</Button>
            </div>
            <p className="font-mono text-[10px] text-white/40 mt-3">Paused & disabled codes return 404 on scan. Printing shows the destination once shipped.</p>
          </Panel>

          <Panel title="// Danger zone">
            <div className="space-y-3">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="unbind-btn" variant="outline" className="w-full border-white/20 text-white/70 bg-transparent hover:bg-secondary hover:text-white justify-start"><LinkBreakIcon weight="bold" className="mr-2" /> Unbind code from my account</Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-card border-border">
                  <AlertDialogHeader><AlertDialogTitle className="font-heading">Unbind /{tag.code}?</AlertDialogTitle>
                    <AlertDialogDescription className="text-white/50">The physical code lives on but returns to an unallocated state. It can be re-bound later. Scan history is kept.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-border hover:bg-secondary hover:text-white">Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid="unbind-confirm" onClick={unbind} className="bg-white text-black hover:bg-white/90">Unbind</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="detail-delete-btn" variant="outline" className="w-full border-destructive/40 text-destructive bg-transparent hover:bg-destructive/10 hover:text-destructive justify-start"><TrashIcon weight="bold" className="mr-2" /> Delete code & all history</Button>
                </AlertDialogTrigger>
                <AlertDialogContent className="bg-card border-border">
                  <AlertDialogHeader><AlertDialogTitle className="font-heading">Delete this code?</AlertDialogTitle>
                    <AlertDialogDescription className="text-white/50">The code /{tag.code}, its links and all scan history will be permanently removed.</AlertDialogDescription></AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel className="bg-transparent border-border hover:bg-secondary hover:text-white">Cancel</AlertDialogCancel>
                    <AlertDialogAction data-testid="detail-delete-confirm-btn" onClick={delTag} className="bg-destructive text-white hover:bg-destructive/90">Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Panel>
        </TabsContent>
      </Tabs>
    </Layout>
  );
}
